import { Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { ConfirmationService } from 'primeng/api';
import { catchError, Observable, of, tap } from 'rxjs';
import { MessageAlertService } from '../notifications/message-alert.service';

export interface ConfirmDialogOptions {
	message: string;
	header?: string;
	/** Lucide icon name (registered in LucideModule), shown above the message */
	icon?: string;
	acceptLabel?: string;
	rejectLabel?: string;
	acceptButtonStyleClass?: string;
	rejectButtonStyleClass?: string;
	accept?: () => void;
	reject?: () => void;
}

export type ConfirmAction = 'delete' | 'disable' | 'enable' | 'archive' | 'restore' | 'approve' | 'reject' | 'cancel';

export interface ConfirmAndExecuteOptions {
	/** Custom confirmation message. If not provided, auto-generated */
	confirmMessage?: string;
	/** Custom header. Default: "Confirm deletion" */
	header?: string;
	/** Custom success message */
	successMessage?: string;
	/** Custom error message */
	errorMessage?: string;
	/** Callback after success (receives the action's response) */
	onSuccess?: (result?: any) => void;
	/** false: no generic success toast (the caller shows its own, e.g. with the server's message) */
	showSuccessToast?: boolean;
	/** Callback after error */
	onError?: (error: any) => void;
	/** Label of the dismiss button (default "Cancel"; e.g. "Back" when the action itself is a cancel) */
	rejectLabel?: string;
}

@Injectable({
	providedIn: 'root',
})
export class ConfirmDialogService {
	constructor(
		private confirmationService: ConfirmationService,
		private messageAlertService: MessageAlertService,
		private translocoService: TranslocoService,
	) {}

	/**
	 * 🚀 MAIN METHOD - Confirms and executes an action with automatic success/error handling
	 *
	 * @example
	 * // Simple usage
	 * this.confirmDialogService.confirmAndExecute(
	 *   'delete',
	 *   this.translocoService.translate('platformsCrud.messages.deleteTarget', { name }),
	 *   () => this.service.delete(id)
	 * );
	 *
	 * @example
	 * // Usage with custom options
	 * this.confirmDialogService.confirmAndExecute(
	 *   'delete',
	 *   this.translocoService.translate('platformsCrud.messages.deleteTargetMany', { count: ids.length }),
	 *   () => this.service.deleteMany(ids),
	 *   {
	 *     onSuccess: () => this.loadData()
	 *   }
	 * );
	 */
	confirmAndExecute<T>(
		action: ConfirmAction,
		target: string,
		executeAction: () => Observable<T>,
		options?: ConfirmAndExecuteOptions,
	): void {
		// Generate default messages based on action
		const defaultMessages = this.getDefaultMessages(action, target);

		const confirmMessage = options?.confirmMessage || defaultMessages.confirm;
		const header = options?.header || defaultMessages.header;
		const successMessage = options?.successMessage || defaultMessages.success;
		const errorMessage = options?.errorMessage || defaultMessages.error;

		this.confirmationService.confirm({
			message: confirmMessage,
			header: header,
			icon: 'TriangleAlert',
			acceptLabel: this.translate(`common.confirm.${action}.accept`),
			rejectLabel: options?.rejectLabel || this.translate('common.actions.cancel'),
			acceptButtonStyleClass: this.getAcceptButtonClass(action),
			accept: () => {
				executeAction()
					.pipe(
						tap((result) => {
							if (options?.showSuccessToast !== false) {
								this.messageAlertService.showMessage(
									'success',
									this.translate('common.labels.success'),
									successMessage,
								);
							}
							options?.onSuccess?.(result);
						}),
						catchError((error) => {
							console.error(`❌ Error ${action}:`, error);
							const finalErrorMessage = error?.error?.message || errorMessage;
							this.messageAlertService.showMessage(
								'error',
								this.translate('common.labels.error'),
								finalErrorMessage,
							);
							options?.onError?.(error);
							return of(null);
						}),
					)
					.subscribe();
			},
		});
	}

	private translate(key: string, params?: Record<string, unknown>): string {
		return this.translocoService.translate(key, params);
	}

	/**
	 * Generates default messages based on action
	 */
	private getDefaultMessages(
		action: ConfirmAction,
		target: string,
	): {
		confirm: string;
		header: string;
		success: string;
		error: string;
	} {
		const base = `common.confirm.${action}`;
		return {
			confirm: this.translate(`${base}.message`, { target }),
			header: this.translate(`${base}.header`),
			success: this.translate(`${base}.success`),
			error: this.translate(`${base}.error`),
		};
	}

	/**
	 * Gets the CSS class for the button based on action
	 */
	private getAcceptButtonClass(action: ConfirmAction): string {
		const classes: Record<ConfirmAction, string> = {
			delete: 'p-button-danger',
			disable: 'p-button-warning',
			enable: 'p-button-success',
			archive: 'p-button-secondary',
			restore: 'p-button-info',
			approve: 'p-button-success',
			reject: 'p-button-danger',
			cancel: 'p-button-danger',
		};
		return classes[action];
	}

	// ========== LEGACY METHODS (keep for compatibility) ==========

	/**
	 * @deprecated Use confirmAndExecute() instead
	 */
	confirmDelete(message: string, onAccept: () => void, header?: string): void {
		this.confirmationService.confirm({
			message,
			header: header || this.translate('common.confirm.delete.header'),
			icon: 'TriangleAlert',
			acceptLabel: this.translate('common.confirm.delete.accept'),
			rejectLabel: this.translate('common.actions.cancel'),
			acceptButtonStyleClass: 'p-button-danger',
			accept: onAccept,
		});
	}

	/**
	 * @deprecated Use confirmAndExecute() instead
	 */
	confirmEnable(message: string, onAccept: () => void, header?: string): void {
		this.confirmationService.confirm({
			message,
			header: header || this.translate('common.confirm.enable.header'),
			icon: 'TriangleAlert',
			acceptLabel: this.translate('common.confirm.enable.accept'),
			rejectLabel: this.translate('common.actions.cancel'),
			acceptButtonStyleClass: 'p-button-success',
			accept: onAccept,
		});
	}

	/**
	 * @deprecated Use confirmAndExecute() instead
	 */
	confirmDisable(message: string, onAccept: () => void, header?: string): void {
		this.confirmationService.confirm({
			message,
			header: header || this.translate('common.confirm.disable.header'),
			icon: 'TriangleAlert',
			acceptLabel: this.translate('common.confirm.disable.accept'),
			rejectLabel: this.translate('common.actions.cancel'),
			acceptButtonStyleClass: 'p-button-warning',
			accept: onAccept,
		});
	}

	/**
	 * @deprecated Use confirmAndExecute() instead
	 */
	confirm(message: string, onAccept: () => void, header?: string, acceptLabel?: string): void {
		this.confirmationService.confirm({
			message,
			header: header || this.translate('common.confirm.genericHeader'),
			icon: 'TriangleAlert',
			acceptLabel: acceptLabel || this.translate('common.actions.accept'),
			rejectLabel: this.translate('common.actions.cancel'),
			accept: onAccept,
		});
	}

	/**
	 * @deprecated Use confirmAndExecute() instead
	 */
	confirmCustom(options: ConfirmDialogOptions): void {
		this.confirmationService.confirm({
			message: options.message,
			header: options.header || this.translate('common.confirm.genericHeader'),
			icon: options.icon || 'TriangleAlert',
			acceptLabel: options.acceptLabel || this.translate('common.actions.accept'),
			rejectLabel: options.rejectLabel || this.translate('common.actions.cancel'),
			acceptButtonStyleClass: options.acceptButtonStyleClass,
			rejectButtonStyleClass: options.rejectButtonStyleClass,
			accept: options.accept,
			reject: options.reject,
		});
	}
}
